/**
 * EventsTab - Manage tournaments, showcases, and multi-game events
 * Refactored to use extracted components for maintainability
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import { useSports } from "../../hooks/useSports";

// Import extracted components
import {
  EVENT_TYPES,
  AllStarTeamsSection,
  BasicInfoSection,
  DateLocationSection,
  BrandingSection,
  BracketsToggleSection,
  BroadcastsSelectSection,
  SponsorsSection,
  VideoPlaylistFormSection,
  StatLeadersFormSection,
  SEOFormSection,
  EventTypeSelector,
  EventListItem,
  TournamentBannerSection
} from "./events";

// Initial form state
const getInitialFormData = () => ({
  name: "",
  slug: "",
  custom_url_path: "",
  event_type: "tournament",
  description: "",
  sport: "Football",
  logo_url: "",
  color: "#FFFFFF",
  start_date: "",
  end_date: "",
  location: "",
  venue: "",
  sponsors: [],
  broadcast_ids: [],
  is_published: false,
  brackets_enabled: false,
  flagship_sponsor_enabled: false,
  flagship_sponsor_name: "",
  flagship_sponsor_logo_url: "",
  flagship_sponsor_url: "",
  tournament_banner_url: "",
  tournament_banner_link: "",
  video_playlist_enabled: false,
  video_playlist_title: "Event Videos",
  video_playlist: [],
  stat_leaders_enabled: false,
  seo: {
    meta_title: "",
    meta_description: "",
    og_image_url: "",
    keywords: []
  },
  all_star_team_1: { name: "", color: "#003087", logo_url: "", roster: [] },
  all_star_team_2: { name: "", color: "#C8102E", logo_url: "", roster: [] },
  roster_tbd: false
});

export const EventsTab = () => {
  const { sports: SPORTS } = useSports(true);
  const [events, setEvents] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState(getInitialFormData());
  const [newSponsor, setNewSponsor] = useState({ name: "", logo_url: "", banner_url: "", logo_file: "", banner_file: "" });
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [newKeyword, setNewKeyword] = useState("");

  const fetchData = async () => {
    try {
      const [eventsRes, broadcastsRes] = await Promise.all([
        axios.get(`${API}/events?include_unpublished=true`),
        axios.get(`${API}/broadcasts/upcoming?limit=50`)
      ]);
      setEvents(eventsRes.data || []);
      setBroadcasts(broadcastsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setFormData(getInitialFormData());
    setNewSponsor({ name: "", logo_url: "", banner_url: "", logo_file: "", banner_file: "" });
    setNewVideoUrl("");
    setNewVideoTitle("");
    setNewKeyword("");
    setEditingEvent(null);
  };

  const handleEdit = async (event) => {
    try {
      const res = await axios.get(`${API}/events/${event.id}`);
      const fullEvent = res.data;
      setFormData({
        name: fullEvent.name || "",
        slug: fullEvent.slug || "",
        custom_url_path: fullEvent.custom_url_path || fullEvent.slug || "",
        event_type: fullEvent.event_type || "tournament",
        description: fullEvent.description || "",
        sport: fullEvent.sport || "Football",
        logo_url: fullEvent.logo_url || "",
        color: fullEvent.color || "#FFFFFF",
        start_date: fullEvent.start_date || "",
        end_date: fullEvent.end_date || "",
        location: fullEvent.location || "",
        venue: fullEvent.venue || "",
        sponsors: fullEvent.sponsors || [],
        broadcast_ids: fullEvent.broadcast_ids || [],
        is_published: fullEvent.is_published || false,
        brackets_enabled: fullEvent.brackets_enabled || false,
        flagship_sponsor_enabled: fullEvent.flagship_sponsor_enabled || false,
        flagship_sponsor_name: fullEvent.flagship_sponsor_name || "",
        flagship_sponsor_logo_url: fullEvent.flagship_sponsor_logo_url || "",
        flagship_sponsor_url: fullEvent.flagship_sponsor_url || "",
        tournament_banner_url: fullEvent.tournament_banner_url || "",
        tournament_banner_link: fullEvent.tournament_banner_link || "",
        video_playlist_enabled: fullEvent.video_playlist_enabled || false,
        video_playlist_title: fullEvent.video_playlist_title || "Event Videos",
        video_playlist: fullEvent.video_playlist || [],
        stat_leaders_enabled: fullEvent.stat_leaders_enabled || false,
        seo: fullEvent.seo || { meta_title: "", meta_description: "", og_image_url: "", keywords: [] },
        all_star_team_1: fullEvent.all_star_team_1 || { name: "", color: "#003087", logo_url: "", roster: [] },
        all_star_team_2: fullEvent.all_star_team_2 || { name: "", color: "#C8102E", logo_url: "", roster: [] },
        roster_tbd: fullEvent.roster_tbd || false
      });
      setEditingEvent(event.id);
      setShowForm(true);
    } catch (error) {
      console.error("Error fetching event:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent}`, formData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/events`, formData, { headers: getAuthHeader() });
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving event:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const handleTogglePublish = async (event) => {
    try {
      await axios.put(`${API}/events/${event.id}`, { is_published: !event.is_published }, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error toggling publish:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Events</h2>
          <p className="text-zinc-500 text-sm">Manage tournaments, showcases, and multi-game events</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" /> New Event
        </Button>
      </div>

      {/* Event Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-white/10 p-6"
        >
          <h3 className="font-secondary text-lg font-bold uppercase text-white mb-6">
            {editingEvent ? "Edit Event" : "Create New Event"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            <EventTypeSelector formData={formData} setFormData={setFormData} />
            <BasicInfoSection formData={formData} setFormData={setFormData} SPORTS={SPORTS} />
            <DateLocationSection formData={formData} setFormData={setFormData} />
            <BrandingSection formData={formData} setFormData={setFormData} />
            <BracketsToggleSection formData={formData} setFormData={setFormData} editingEvent={editingEvent} />
            
            {formData.event_type === 'all_star' && (
              <AllStarTeamsSection formData={formData} setFormData={setFormData} />
            )}

            {/* Logo Preview */}
            {formData.logo_url && (
              <div className="flex items-center gap-4 p-4 bg-zinc-800/50 border border-white/10">
                <img src={formData.logo_url} alt="Event Logo" className="h-16 max-w-[200px] object-contain" />
                <span className="text-xs text-zinc-500">Logo Preview</span>
              </div>
            )}

            <SponsorsSection 
              formData={formData} 
              setFormData={setFormData} 
              newSponsor={newSponsor} 
              setNewSponsor={setNewSponsor} 
            />
            
            <TournamentBannerSection formData={formData} setFormData={setFormData} />
            
            <BroadcastsSelectSection 
              formData={formData} 
              setFormData={setFormData} 
              broadcasts={broadcasts} 
            />
            
            <VideoPlaylistFormSection 
              formData={formData} 
              setFormData={setFormData}
              newVideoUrl={newVideoUrl}
              setNewVideoUrl={setNewVideoUrl}
              newVideoTitle={newVideoTitle}
              setNewVideoTitle={setNewVideoTitle}
            />
            
            <StatLeadersFormSection formData={formData} setFormData={setFormData} />
            
            <SEOFormSection 
              formData={formData} 
              setFormData={setFormData}
              newKeyword={newKeyword}
              setNewKeyword={setNewKeyword}
            />

            {/* Form Actions */}
            <div className="flex items-center gap-4 pt-4 border-t border-white/10">
              <Button type="submit" disabled={saving} className="bg-white text-black hover:bg-zinc-200 text-xs px-6 py-2 h-auto rounded-none">
                {saving ? "Saving..." : editingEvent ? "Update Event" : "Create Event"}
              </Button>
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="text-zinc-500 text-sm hover:text-white">
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Events List */}
      <div className="space-y-4">
        {events.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Trophy className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No events created yet</p>
            <p className="text-xs text-zinc-600 mt-2">Create your first tournament or showcase</p>
          </div>
        ) : (
          events.map(event => (
            <EventListItem
              key={event.id}
              event={event}
              isExpanded={expandedEvent === event.id}
              onToggleExpand={(id) => setExpandedEvent(expandedEvent === id ? null : id)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTogglePublish={handleTogglePublish}
            />
          ))
        )}
      </div>
    </motion.div>
  );
};

export default EventsTab;
